/**
 * New versions of the app.
 *
 * The service worker precaches the bundle, which is what makes the app open
 * with no signal — and also what kept the owner's Surface on an old copy
 * after two deploys (23 September 2026): the worker serves the cached page
 * first and only installs the new one behind it, so a fix needs a second
 * full close-and-open to show, and nothing on screen says so.
 *
 * So the app asks for the update itself:
 *   - on the login screen, where nothing is in progress, a waiting version is
 *     applied at once and the page reloads into it;
 *   - while someone is signed in, a banner offers "Update now" and nothing
 *     reloads under her;
 *   - the worker is asked to check again every hour and whenever the app is
 *     brought back to the front, so a tablet left open still hears.
 */

import { useEffect, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';

type Listener = (ready: boolean) => void;

let ready = false;
let apply: ((reloadPage?: boolean) => Promise<void>) | null = null;
const listeners = new Set<Listener>();

export function startUpdates(): void {
  if (apply || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  apply = registerSW({
    immediate: true,
    onNeedRefresh() {
      ready = true;
      listeners.forEach((listener) => listener(true));
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => void registration.update().catch(() => undefined);
      setInterval(check, 60 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
    },
  });
}

/** Take the waiting version now; the page reloads into it. */
export function applyUpdate(): Promise<void> {
  return apply ? apply(true) : Promise.resolve();
}

export function useUpdateReady(): boolean {
  const [state, setState] = useState(ready);
  useEffect(() => {
    listeners.add(setState);
    return () => { listeners.delete(setState); };
  }, []);
  return state;
}
